import type { QueryClient } from '@tanstack/react-query';

import { loadBulkProcessQueue, mergeBulkTargetRefs, saveBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import {
  activeBulkBatchIdSet,
  coalesceInflightApplicationStates,
  expandProcessingIdsForBulkTargets,
  hydrateInFlightBatchFromDetail,
  hydrateUnsettledBulkTargetsFromDetail,
  reconcileQueueIds,
  RESUME_HYDRATE_MAX_PER_SYNC,
} from '@/lib/bulkProcessQueueSync';
import {
  resetBulkListRefetchThrottle,
  shouldRefetchApplicationsListDuringBulk,
} from '@/lib/bulkQueuePoll';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { queryKeys } from '@/lib/queryKeys';
import type { Application } from '@/types';

export type SyncBulkQueueProgressOptions = {
  targetIds?: readonly string[];
  /** Force a list refetch before hydrate (F5 resume). */
  forceListRefetch?: boolean;
  /** Hydrate all batch targets in one pass after reload. */
  resumeHydrate?: boolean;
};

/** Poll path: refetch list, hydrate batch via detail API, reconcile queue, patch cache + session. */
export async function syncBulkQueueProgress(
  queryClient: QueryClient,
  companyId: string,
  syncBanner: (
    companyId: string,
    processingIds: Set<string>,
    stoppingIds: Set<string>,
    applications?: Application[],
  ) => void,
  options?: SyncBulkQueueProgressOptions,
): Promise<void> {
  const loaded = loadBulkProcessQueue(companyId);
  const targetIds = options?.targetIds?.length
    ? [...options.targetIds]
    : loaded.targetIds;

  const listSync = createApplicationListSync(queryClient);
  let apps = listSync.getFlattenedListApplications(companyId, false);

  const forceRefetch = options?.forceListRefetch === true;
  const shouldRefetch =
    forceRefetch
    || apps.length === 0
    || shouldRefetchApplicationsListDuringBulk(companyId);

  if (shouldRefetch) {
    if (forceRefetch) {
      resetBulkListRefetchThrottle(companyId);
    }
    await queryClient.refetchQueries({
      queryKey: ['applications-list', companyId],
      type: 'active',
    });
    await queryClient.refetchQueries({
      queryKey: queryKeys.applications(companyId, false),
      type: 'all',
    });
    apps = listSync.getFlattenedListApplications(companyId, false);
  }

  const batchScope = activeBulkBatchIdSet(
    targetIds,
    loaded.processingIds,
    loaded.stoppingIds,
  );
  const scopedProcessing = new Set(
    [...loaded.processingIds].filter((id) => batchScope.has(id)),
  );
  const scopedStopping = new Set(
    [...loaded.stoppingIds].filter((id) => batchScope.has(id)),
  );

  const hydrateMax = options?.resumeHydrate
    ? Math.min(RESUME_HYDRATE_MAX_PER_SYNC, Math.max(targetIds.length, 1))
    : undefined;

  // List API often lags — detail endpoint matches Application detail page truth.
  if (loaded.targetIds.length > 0) {
    apps = await hydrateUnsettledBulkTargetsFromDetail(
      companyId,
      apps,
      loaded.targetIds,
      {
        maxPerSync: hydrateMax !== undefined ? hydrateMax : undefined,
        targetRefs: loaded.targetRefs,
      },
    );
  } else if (scopedProcessing.size > 0) {
    apps = await hydrateInFlightBatchFromDetail(
      companyId,
      apps,
      loaded.targetIds,
      scopedProcessing,
      loaded.targetRefs,
    );
  }

  const reconciled = reconcileQueueIds(apps, scopedStopping, scopedProcessing, {
    bulkTargetIds: batchScope,
  });
  const expandedProcessing = expandProcessingIdsForBulkTargets(
    targetIds,
    reconciled.processingIds,
    apps,
    { includeUnknownTargets: apps.length > 0 },
  );

  const mergedTargetRefs = mergeBulkTargetRefs(loaded.targetRefs, apps, targetIds);
  saveBulkProcessQueue(
    companyId,
    reconciled.stoppingIds,
    expandedProcessing,
    targetIds,
    undefined,
    mergedTargetRefs,
  );
  listSync.patchCompanyUpdater(companyId, (prev) => {
    const merged = mergeApplicationsById(prev, apps);
    if (expandedProcessing.size === 0) {
      return merged;
    }
    return coalesceInflightApplicationStates(merged, expandedProcessing);
  }, false);
  syncBanner(companyId, expandedProcessing, reconciled.stoppingIds, apps);
}
